import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client.js';
import './CoachQuestionsBank.css';

export const COACH_QUESTION_CATEGORIES = {
    escuchar: '👂 Escucha',
    error: '🤲 Procesa',
    adaptarse: '🌊 Ajusta',
    proposito: '🎯 Propósito',
    constante: '🟢 Perfil Constante',
    impulsivo: '🔴 Perfil Impulsivo',
    analitico: '🔵 Perfil Analítico',
    social: '🟡 Perfil Social',
};

const MIN_PER_CATEGORY = 10;

export default function CoachQuestionsBank({ autoEnsureBank = true }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [ensuring, setEnsuring] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [editing, setEditing] = useState(null);
    const [newQ, setNewQ] = useState({
        category: 'escuchar',
        question_text: '',
        purpose: '',
    });

    const loadBank = useCallback(async () => {
        const res = await api.getCoachQuestionsBank();
        setRows(res?.data || []);
    }, []);

    const ensureBank = useCallback(async (silent = false) => {
        setEnsuring(true);
        try {
            const result = await api.ensureCoachQuestionBank();
            if (!silent && result?.inserted > 0) {
                alert(`Se añadieron ${result.inserted} preguntas (mínimo ${MIN_PER_CATEGORY} por categoría).`);
            }
            await loadBank();
            return result;
        } catch (err) {
            if (!silent) alert(err.message || 'No se pudo completar el banco');
            throw err;
        } finally {
            setEnsuring(false);
        }
    }, [loadBank]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                if (autoEnsureBank) {
                    try {
                        await api.ensureCoachQuestionBank();
                    } catch (e) {
                        console.warn('coach ensure-bank', e);
                    }
                }
                if (!cancelled) await loadBank();
            } catch (e) {
                console.error(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [autoEnsureBank, loadBank]);

    const filtered =
        selectedCategory === 'all' ? rows : rows.filter((r) => r.category === selectedCategory);

    const countByCategory = rows.reduce((acc, r) => {
        if (r.is_active === 0) return acc;
        acc[r.category] = (acc[r.category] || 0) + 1;
        return acc;
    }, {});

    async function handleSave(e) {
        e.preventDefault();
        try {
            if (editing) {
                await api.updateCoachQuestion(editing.id, editing);
            } else {
                await api.createCoachQuestion(newQ);
                setNewQ({
                    category: selectedCategory !== 'all' ? selectedCategory : 'escuchar',
                    question_text: '',
                    purpose: '',
                });
            }
            setEditing(null);
            await loadBank();
        } catch (err) {
            alert('Error al guardar: ' + (err.message || 'desconocido'));
        }
    }

    async function handleDelete(id) {
        if (!window.confirm('¿Eliminar esta pregunta del banco maestro?')) return;
        try {
            await api.deleteCoachQuestion(id);
            await loadBank();
        } catch (err) {
            alert(err.message || 'No se pudo eliminar');
        }
    }

    if (loading) {
        return <p className="coach-bank-hint">Cargando banco de preguntas…</p>;
    }

    return (
        <div className="coach-bank-admin">
            <div className="coach-bank-toolbar">
                <p className="text-muted coach-bank-lead">
                    Banco maestro editable. Mínimo {MIN_PER_CATEGORY} preguntas activas por categoría. Se usa en
                    supervisor, coach y conversaciones con la manada.
                </p>
                <button type="button" className="btn btn-secondary btn-sm" disabled={ensuring} onClick={() => ensureBank(false)}>
                    {ensuring ? 'Completando…' : 'Completar banco (10/categoría)'}
                </button>
            </div>

            <div className="coach-bank-category-pills">
                <button
                    type="button"
                    className={selectedCategory === 'all' ? 'pill active' : 'pill'}
                    onClick={() => setSelectedCategory('all')}
                >
                    Todas ({rows.length})
                </button>
                {Object.entries(COACH_QUESTION_CATEGORIES).map(([key, label]) => {
                    const n = countByCategory[key] || 0;
                    const low = n < MIN_PER_CATEGORY;
                    return (
                        <button
                            key={key}
                            type="button"
                            className={selectedCategory === key ? 'pill active' : 'pill'}
                            onClick={() => setSelectedCategory(key)}
                            title={low ? `Faltan ${MIN_PER_CATEGORY - n}` : undefined}
                        >
                            {label} ({n}){low ? ' ⚠' : ''}
                        </button>
                    );
                })}
            </div>

            <form className="glass-card coach-bank-form" onSubmit={handleSave}>
                <h4>{editing ? 'Editar pregunta' : 'Nueva pregunta'}</h4>
                <div className="coach-bank-form-row">
                    <label>
                        Categoría
                        <select
                            value={editing ? editing.category : newQ.category}
                            onChange={(e) =>
                                editing
                                    ? setEditing({ ...editing, category: e.target.value })
                                    : setNewQ({ ...newQ, category: e.target.value })
                            }
                        >
                            {Object.entries(COACH_QUESTION_CATEGORIES).map(([k, lbl]) => (
                                <option key={k} value={k}>
                                    {lbl}
                                </option>
                            ))}
                        </select>
                    </label>
                    <label className="flex-1">
                        Pregunta
                        <input
                            type="text"
                            required
                            value={editing ? editing.question_text : newQ.question_text}
                            onChange={(e) =>
                                editing
                                    ? setEditing({ ...editing, question_text: e.target.value })
                                    : setNewQ({ ...newQ, question_text: e.target.value })
                            }
                            placeholder="¿Cómo estás hoy realmente?"
                        />
                    </label>
                </div>
                <div className="coach-bank-form-actions">
                    <button type="submit" className="btn btn-primary btn-sm">
                        {editing ? 'Guardar cambios' : 'Agregar'}
                    </button>
                    {editing && (
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditing(null)}>
                            Cancelar
                        </button>
                    )}
                </div>
            </form>

            <ul className="coach-bank-list">
                {filtered.map((row) => (
                    <li key={row.id} className="coach-bank-item glass-card">
                        <span className="coach-bank-cat">{COACH_QUESTION_CATEGORIES[row.category] || row.category}</span>
                        <p className="coach-bank-text">"{row.question_text}"</p>
                        <div className="coach-bank-item-actions">
                            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditing({ ...row })}>
                                Editar
                            </button>
                            <button type="button" className="btn btn-secondary btn-sm" onClick={() => handleDelete(row.id)}>
                                Eliminar
                            </button>
                        </div>
                    </li>
                ))}
            </ul>
            {filtered.length === 0 && <p className="coach-bank-hint">No hay preguntas en esta categoría.</p>}
        </div>
    );
}
